import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, HardHat, HeartHandshake, CheckCircle2, ArrowRight, Wrench, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useApi, PageHeader, Panel, StatusChip, Empty, Field, inputCls, btn } from "../components/kit";

const CATS = ["Hazard", "Near miss", "Incident", "Equipment problem", "Chemical / spill", "Other safety concern"];

export function EmergencyNotice({ settings }) {
  return (
    <div role="note" data-testid="emergency-notice" className="flex gap-3 rounded-2xl border-2 border-rose-300 bg-rose-50 p-5 text-rose-950">
      <AlertOctagon className="h-6 w-6 shrink-0" aria-hidden="true" />
      <div>
        <p className="font-semibold">In a life-threatening emergency, call 9-1-1.</p>
        <p className="text-sm mt-1">Everything you send here is seen and followed up. If someone is in danger right now, get to safety and call 9-1-1 first, then report it.</p>
      </div>
    </div>
  );
}

function HazardForm({ onDone }) {
  const [f, setF] = useState({ category: "Hazard", description: "", location: "", occurred_at: "", equipment: "", action_taken: "", danger_remains: "unsure", someone_hurt: false });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    files.forEach((x) => fd.append("files", x));
    setBusy(true);
    try { const r = await api.post("/safety-reports", fd); setDone(r.data); onDone(); window.scrollTo(0, 0); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6" data-testid="safety-confirmation" role="status">
      <CheckCircle2 className="h-8 w-8 text-emerald-700" />
      <p className="mt-2 text-xl font-semibold">Report received</p>
      <p className="mt-1">Reference <b data-testid="safety-confirmation-ref">{done.ref}</b> · {fmtDateTime(done.created_at)}</p>
      <p className="text-sm mt-2 text-emerald-900">It has gone to the safety coordinator and backup. You'll see shareable updates under “My reports”.</p>
      <button className={`${btn.ghost} mt-4`} onClick={() => setDone(null)} data-testid="safety-new-report-button">Report something else</button>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="safety-form">
      <Field label="What kind of concern?" htmlFor="cat"><select id="cat" className={inputCls} value={f.category} onChange={set("category")} data-testid="safety-category-select">{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
      <Field label="What happened or what did you see?" htmlFor="desc"><textarea id="desc" required rows={4} className={inputCls} value={f.description} onChange={set("description")} data-testid="safety-description-input" /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Where?" htmlFor="loc"><input id="loc" required className={inputCls} value={f.location} onChange={set("location")} data-testid="safety-location-input" /></Field>
        <Field label="When?" htmlFor="when"><input id="when" type="datetime-local" className={inputCls} value={f.occurred_at} onChange={set("occurred_at")} data-testid="safety-when-input" /></Field>
        <Field label="Equipment or chemical involved" htmlFor="eq"><input id="eq" className={inputCls} value={f.equipment} onChange={set("equipment")} data-testid="safety-equipment-input" /></Field>
        <Field label="Action already taken" htmlFor="act"><input id="act" className={inputCls} value={f.action_taken} onChange={set("action_taken")} data-testid="safety-action-input" /></Field>
      </div>
      <fieldset><legend className="text-sm font-semibold mb-2">Does danger remain?</legend>
        <div className="flex flex-wrap gap-2">{[["yes", "Yes"], ["no", "No"], ["unsure", "Not sure"]].map(([v, l]) => (
          <label key={v} className={`tap inline-flex items-center gap-2 rounded-lg border px-4 cursor-pointer ${f.danger_remains === v ? "border-[color:var(--ink)] bg-muted" : "bg-white"}`}>
            <input type="radio" name="dr" checked={f.danger_remains === v} onChange={() => setF({ ...f, danger_remains: v })} data-testid={`safety-danger-${v}`} className="h-5 w-5 accent-[color:var(--dcbc-red)]" /> {l}</label>))}</div>
      </fieldset>
      <label className="flex items-center gap-3"><input type="checkbox" checked={f.someone_hurt} onChange={(e) => setF({ ...f, someone_hurt: e.target.checked })} data-testid="safety-hurt-checkbox" className="h-5 w-5 accent-[color:var(--dcbc-red)]" /> Someone was hurt <span className="text-xs text-muted-foreground">(please don't include medical details)</span></label>
      <Field label="Photos (optional)" htmlFor="ph" hint="Up to 6 photos"><input id="ph" type="file" multiple accept="image/*,.heic,.heif" onChange={(e) => setFiles([...e.target.files])} data-testid="safety-photos-input" className="block w-full text-sm" /></Field>
      <button className={`${btn.primary} w-full sm:w-auto`} disabled={busy} data-testid="safety-submit-button">{busy ? "Sending…" : "Submit report"}</button>
    </form>
  );
}

export default function Concern() {
  const settings = useApi("/settings");
  const mine = useApi("/safety-reports");
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Speak up" title="Report a Concern" testid="concern-header" />
      <EmergencyNotice settings={settings.data} />
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Panel title={<span className="flex items-center gap-2"><HardHat className="h-5 w-5" /> Safety hazard, near miss or incident</span>} testid="safety-panel">
          <HazardForm onDone={mine.reload} />
        </Panel>
        <div className="space-y-6">
          <Link to="/concern/help" data-testid="help-page-link" className="block rounded-2xl bg-[color:var(--ink)] p-6 text-white hover:bg-black transition-colors">
            <HeartHandshake className="h-7 w-7" />
            <p className="mt-3 font-display text-xl font-semibold">Harassment or interpersonal concern</p>
            <p className="text-sm text-white/75 mt-1">Get help and reporting options. These concerns never go to managers or the hazard register.</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">Help and Reporting <ArrowRight className="h-4 w-4" /></span>
          </Link>
          <a href="https://dcbc-exec-dash.emergent.host/maintenance/requests" target="_blank" rel="noreferrer" data-testid="maintenance-link"
            className="block rounded-2xl border-2 border-[color:var(--ink)] bg-white p-6 hover:bg-muted/40 transition-colors">
            <Wrench className="h-7 w-7 text-[color:var(--dcbc-red)]" />
            <p className="mt-3 font-display text-xl font-semibold">Something broken or needs fixing?</p>
            <p className="text-sm text-muted-foreground mt-1">Equipment, building or facilities repairs that aren't a safety hazard go on our maintenance request form.</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">Maintenance request <ExternalLink className="h-4 w-4" /></span>
          </a>
          <Panel title="My reports" testid="my-reports">
            {(mine.data || []).length === 0 ? <Empty>You haven't submitted any safety reports.</Empty> : (
              <ul className="divide-y">{mine.data.map((r) => (
                <li key={r.id}><Link to={`/concern/reports/${r.id}`} className="flex items-center justify-between gap-3 py-3" data-testid={`my-report-${r.ref}`}>
                  <span><span className="font-semibold">{r.ref}</span><span className="block text-xs text-muted-foreground">{r.category} · {fmtDateTime(r.created_at)}</span></span>
                  <StatusChip status={r.status} /></Link></li>))}</ul>)}
          </Panel>
        </div>
      </div>
    </div>
  );
}
